import classNames from 'classnames/bind';
import React, { useMemo, useState } from 'react';
import { RiVideoOnLine } from 'react-icons/ri';
import { v4 as uuidv4 } from 'uuid';

import styles from './index.module.scss';

import Select from '@/components/ui/kit/Select';
import LabelGroup from '@/components/ui/LabelGroup';
import LiveViewWindow from '@/components/windows/LiveViewDJI';
import { STREAMHUB_RTMP_URL } from '@/config/index';
import useVideoCommand from '@/hooks/command/dji/useVideoCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { windowsManager } from '@/pages/control-center/Windows';
import { executeDJICommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: [robot] }) => {
  const [sourceIndex, setSourceIndex] = useState(0);
  const videoCommand = useVideoCommand();

  const sources = useMemo(() => {
    return [
      {
        label: 'Drone',
        sn: robot.metadata['DJI_AIRCRAFT_SN'],
        cameraIndex: '99-0-0',
        videoIndex: 'normal-0',
      },
      {
        label: 'Dock',
        sn: robot.metadata['DJI_DOCK_SN'],
        cameraIndex: '165-0-7',
        videoIndex: 'normal-0',
      },
    ];
  }, [robot]);

  const changeSource = (e) => {
    setSourceIndex(e.target.value);
  };

  //#region Process Functions
  const processStart = async () => {
    const { label, sn, cameraIndex, videoIndex } = sources[sourceIndex];

    let showWindow = false;

    try {
      const rtmpUrl = `${STREAMHUB_RTMP_URL}/live/stream/${robot.id}/${sn}`;
      const videoId = [sn, cameraIndex, videoIndex].join('/');
      await executeDJICommand(robot, videoCommand.streamingStart(rtmpUrl, videoId));

      showWindow = true;
      onSuccess(robot, 'started');
    } catch (error) {
      // 513003: Camera livestream in progress.
      if (error.data.result === 513003) {
        showWindow = true;
      } else {
        if (typeof error === 'object' && error?.data?.message) {
          console.debug(error.data.message);
        }
        onFailure(robot, 'start');
      }
    }

    if (showWindow) {
      const windowId = `${robot.id}/liveView/${label.toLowerCase()}`;
      const title = `Live View - ${label}`;

      windowsManager.show(LiveViewWindow, windowId, { title, color: robot.color }, { robot, sn });
    }
  };

  const processStop = async () => {
    const { label, sn, cameraIndex, videoIndex } = sources[sourceIndex];
    const videoId = [sn, cameraIndex, videoIndex].join('/');
    const windowId = `${robot.id}/liveView/${label.toLowerCase()}`;

    try {
      await executeDJICommand(robot, videoCommand.streamingStop(videoId));
      windowsManager.hide(windowId);

      onSuccess(robot, 'stopped');
    } catch (error) {
      if (typeof error === 'object' && error?.data?.message) {
        console.debug(error.data.message);
      }
      onFailure(robot, 'stop');
    }
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot, word) => {
    notifier.success(`${robot.name} streaming ${word} successfully.`);
  };

  const onFailure = (robot, word) => {
    notifier.error(`Failed to ${word} streaming of ${robot.name}.`);
  };
  //#endregion

  return (
    <div className={cx('container')}>
      <div className={cx('form')}>
        <LabelGroup htmlFor="source" label="Source">
          <Select id="source" value={sourceIndex} onChange={changeSource}>
            {sources.map((source, index) => (
              <option key={index} value={index}>
                {source.label}
              </option>
            ))}
          </Select>
        </LabelGroup>
      </div>
      <div className={cx('buttons')}>
        <button type="button" className={cx('button')} onClick={processStart}>
          Start
        </button>
        <button type="button" className={cx('button')} onClick={processStop}>
          Stop
        </button>
      </div>
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Live View';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = false;
Command.Icon = RiVideoOnLine;
Command.Panel = Panel;

export default Command;
